<template>
  <div>
    <q-linear-progress
    v-if="loading"
    color="primary"
    indeterminate
    />

    <q-list
      v-if="mode === 'single'"
      bordered
      separator
      class="rounded-borders"
      style="max-height: 300px; overflow-y: auto;"
    >
      <q-item
        v-for="person in persons"
        :key="person.id"
        clickable
        :active="modelValue.includes(person.id)"
        @click="$emit('update:modelValue', [person.id])"
      >
        <q-item-section avatar>
          <q-avatar size="40px" color="grey-3" text-color="grey-6">
            <q-img
              v-if="person.primaryFace && person.primaryFace.imageUrl"
              :src="person.primaryFace.imageUrl"
              fit="cover"
              :class="{ 'grayscale': modelValue.includes(person.id) }"
            />
            <q-icon v-else name="fa fa-user" />
          </q-avatar>
        </q-item-section>
        <q-item-section>
          <q-item-label>{{ person.label || 'Unnamed Person' }}</q-item-label>
          <q-item-label caption>{{ person.faceCount || 0 }} faces</q-item-label>
        </q-item-section>
        <q-item-section side>
          <q-radio
            :model-value="modelValue[0]"
            :val="person.id"
            @update:model-value="$emit('update:modelValue', [$event])"
          />
        </q-item-section>
      </q-item>
    </q-list>

    <div v-else class="person-picker-grid">
      <PersonAvatarItem
        v-for="person in persons"
        :key="person.id"
        :person="person"
        :selected="isSelected(person.id)"
        size="88px"
        @click="toggleMulti(person.id)"
      />
    </div>

    <InfiniteScroll
      :active="hasMore"
      :loading="loadingMore"
      class="q-mt-xs"
      @load-more="loadMore"
    />

    <div
      v-if="!loading && persons.length === 0"
      class="text-center text-bold q-pa-sm"
    >
      {{ emptyLabel }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import _ from 'lodash';
import { Person } from '@/analysis/types';
import InfiniteScroll from '@/components/InfiniteScroll.vue';
import PersonAvatarItem from './PersonAvatarItem.vue';
import personRepository from './person-repository';

interface Data {
  persons: Person[];
  loading: boolean;
  loadingMore: boolean;
  total: number;
  offset: number;
}

const PAGE_SIZE = 50;

export default defineComponent({
  emits: ['loaded', 'update:modelValue'],
  components: {
    InfiniteScroll,
    PersonAvatarItem,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
    mode: {
      type: String,
      default: 'multi',
      validator: (v: string) => ['single', 'multi'].includes(v),
    },
    modelValue: {
      type: Array as () => number[],
      required: true,
    },
    keyword: {
      type: String,
      default: '',
    },
    emptyLabel: {
      type: String,
      default: 'No people detected yet',
    },
  },
  data(): Data {
    return {
      persons: [],
      loading: false,
      loadingMore: false,
      total: 0,
      offset: 0,
    };
  },
  computed: {
    hasMore(): boolean {
      return this.persons.length < this.total;
    },
  },
  watch: {
    keyword: _.debounce(function dLoad(this: any) {
      this.load();
    }, 300),
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const params: Record<string, unknown> = {
          limit: PAGE_SIZE,
          offset: 0,
        };
        if (this.keyword) {
          params.keyword = this.keyword;
        }
        const result = await personRepository.findAll(this.clientId, params);
        this.persons = result.items;
        this.total = result.total;
        this.offset = PAGE_SIZE;
        this.$emit('loaded');
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      this.loadingMore = true;
      try {
        const params: Record<string, unknown> = {
          limit: PAGE_SIZE,
          offset: this.offset,
        };
        if (this.keyword) {
          params.keyword = this.keyword;
        }
        const result = await personRepository.findAll(this.clientId, params);
        this.persons.push(...result.items);
        this.offset += PAGE_SIZE;
      } finally {
        this.loadingMore = false;
      }
    },

    isSelected(personId: number): boolean {
      return this.modelValue.includes(personId);
    },

    toggleMulti(personId: number) {
      const selected = [...this.modelValue];
      const index = selected.indexOf(personId);
      if (index !== -1) {
        selected.splice(index, 1);
      } else {
        selected.push(personId);
      }
      this.$emit('update:modelValue', selected);
    },

    addPerson(person: Person) {
      this.persons.unshift(person);
      this.total += 1;
    },

    getPersonsByIds(ids: number[]): Person[] {
      return this.persons.filter(p => ids.includes(p.id));
    },
  },
});
</script>

<style scoped>
.person-picker-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  justify-items: center;
  padding: 4px;
}

.grayscale :deep(img) {
  filter: grayscale(100%);
}
</style>
